import {
  DEFAULT_STATUS_FILTERS,
  ONBOARDING_GROUP_KEYS,
  ONBOARDING_EMPTY_AGENCY
} from './onboardingDashboardConstants';

export function getOnboardingRowId(row) {
  return `${row.agentAgencyId ?? row.agentId ?? ''}-${row.npn}`;
}

const GROUP_LAST_COMPLETED_STEP = {
  onboardingSent: 1,
  onboardingSubmitted: 2,
  paSigned: 3,
  onboardingCompleted: 4,
  onboardingDenied: -1
};

function getRowLastCompletedStep(row) {
  const step = Number(row?.lastCompletedStep);
  if (Number.isFinite(step) && step > 0) return step;
  return GROUP_LAST_COMPLETED_STEP[row?.groupKey] ?? 0;
}

export function sortOnboardingRows(rows) {
  return [...(rows ?? [])].sort((a, b) => {
    const aDenied = a.groupKey === 'onboardingDenied';
    const bDenied = b.groupKey === 'onboardingDenied';
    if (aDenied !== bDenied) return aDenied ? 1 : -1;

    const stepDiff = getRowLastCompletedStep(a) - getRowLastCompletedStep(b);
    if (stepDiff !== 0) return stepDiff;

    return String(a.agentName ?? '').localeCompare(
      String(b.agentName ?? ''),
      undefined,
      { sensitivity: 'base' }
    );
  });
}

/**
 * Flatten one API success payload into normalized rows (npn + groupKey + record fields).
 * @param {object} onboardingInformation - parsed.onboardingInformation
 * @param {string|null|undefined} responseAgencyId - agencyId from API root (same level as onboardingInformation) or fallback
 */
export function flattenOnboardingInformation(
  onboardingInformation,
  responseAgencyId = null
) {
  if (!onboardingInformation?.groups) return [];
  const out = [];

  const payloadAgencyId =
    responseAgencyId ?? onboardingInformation?.agencyId ?? null;

  for (const groupKey of ONBOARDING_GROUP_KEYS) {
    const group = onboardingInformation.groups[groupKey];
    if (!group?.records) continue;
    const entries = Object.entries(group.records);
    for (const [npn, record] of entries) {
      out.push({
        npn,
        groupKey,
        groupLabel: group.label || groupKey,
        lastCompletedStep: group.lastCompletedStep,
        ...record,
        onboardingPayloadAgencyId: payloadAgencyId
      });
    }
  }

  return out;
}

/**
 * Builds the agency filter options from the fetched rows.
 * @param {Array} rows - flattened onboarding rows
 * @returns {string[]} sorted distinct agency names; ONBOARDING_EMPTY_AGENCY pinned first when any row has a blank agency
 */
export function buildAgencyFilterOptions(rows) {
  const names = new Set();
  let hasBlank = false;
  for (const r of rows ?? []) {
    const name = String(r?.relatedToAgency ?? '').trim();
    if (name) names.add(name);
    else hasBlank = true;
  }
  const sorted = [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  return hasBlank ? [ONBOARDING_EMPTY_AGENCY, ...sorted] : sorted;
}

/**
 * Applies search, status and agency filters to the onboarding rows, then sorts.
 * @param {Array} rows - flattened onboarding rows
 * @param {object} filters - { searchRaw, statusFilters, agencyFilters }
 */
export function applyOnboardingFilters(
  rows,
  { searchRaw, statusFilters = DEFAULT_STATUS_FILTERS, agencyFilters = null }
) {
  let list = [...(rows ?? [])];
  const q = searchRaw.trim().toLowerCase();
  if (q) {
    list = list.filter(r => {
      const name = (r.agentName || '').toLowerCase();
      const npn = String(r.npn ?? '');
      return name.includes(q) || npn.includes(q);
    });
  }
  // empty status selection now means zero rows (no implicit "show all")
  if (Array.isArray(statusFilters)) {
    const allowed = new Set(statusFilters);
    list = list.filter(r => allowed.has(r.groupKey));
  }
  // agencyFilters null = no agency filtering; [] = zero rows
  if (Array.isArray(agencyFilters)) {
    const allowed = new Set(agencyFilters);
    list = list.filter(r => {
      const name = String(r.relatedToAgency ?? '').trim();
      return name ? allowed.has(name) : allowed.has(ONBOARDING_EMPTY_AGENCY);
    });
  }
  return sortOnboardingRows(list);
}

/**
 * Whether any filter narrows the default view (drives the "no matches" empty state).
 * @param {object} filters - { searchRaw, statusFilters, agencyFilters, agencyOptions }
 */
export function onboardingFiltersActive({
  searchRaw,
  statusFilters = DEFAULT_STATUS_FILTERS,
  agencyFilters = null,
  agencyOptions = []
}) {
  const defaultSet = new Set(DEFAULT_STATUS_FILTERS);
  const currentSet = new Set(statusFilters ?? []);
  const statusDiffersFromDefault =
    defaultSet.size !== currentSet.size ||
    DEFAULT_STATUS_FILTERS.some(key => !currentSet.has(key)) ||
    [...currentSet].some(key => !defaultSet.has(key));

  // default agency state = all options selected, so any narrowing counts as active
  const agencyNarrowed =
    Array.isArray(agencyFilters) &&
    new Set(agencyFilters).size !== agencyOptions.length;

  return searchRaw.trim() !== '' || statusDiffersFromDefault || agencyNarrowed;
}
